import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { useGridSettingsSafe } from '../store/selectors/useGridSettingsSafe';
import { useEagGridStore } from '../store/useEagGridStore';
import type { EagColumn } from '../types/EagColumn';
import { measureColumnContentWidth } from '../utils/autosizeColumn';
import { getHeaderButtonWidth } from '../utils/getHeaderButtonWidth';
type UseColumnAutosizeReturn = {
  autosizeColumn: (key: string) => void;
  autosizeAllColumns: () => void;
};

/**
 * Provides column auto-sizing callbacks that dynamically measure content width.
 *
 * Reads `headerHeight` from the grid settings store and accepts `showColumnMenu`
 * as a parameter to compute the exact header button region width — no hardcoded
 * padding constants.
 *
 * The measurement clones each cell into an offscreen container to read the
 * unconstrained `offsetWidth`, which already includes all CSS padding and
 * renderer-internal spacing (chips, flags, icons, etc.).
 *
 * @param tableRef - Ref to the rendered `<table>` element
 * @param flatColumnMap - Stable Map<key, EagColumn> for looking up per-column minWidth
 * @param showColumnMenu - Whether the column menu (sort + menu buttons) is visible in header cells
 */
export const useColumnAutosize = (tableRef, flatColumnMap, showColumnMenu) => {
  const $ = _c(16);
  const store = useEagGridStore();
  const gridSettings = useGridSettingsSafe();
  let t0;
  if ($[0] !== gridSettings.headerHeight || $[1] !== showColumnMenu) {
    t0 = getHeaderButtonWidth(gridSettings.headerHeight, showColumnMenu);
    $[0] = gridSettings.headerHeight;
    $[1] = showColumnMenu;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const headerButtonWidth = t0;
  let t1;
  if ($[3] !== flatColumnMap || $[4] !== headerButtonWidth || $[5] !== store || $[6] !== tableRef) {
    t1 = key => {
      if (!tableRef.current) {
        return;
      }
      const col = flatColumnMap.get(key);
      const width = measureColumnContentWidth(tableRef.current, key, {
        minWidth: col?.minWidth,
        headerButtonWidth
      });
      if (width != null) {
        store.setColumnWidth(key, width);
      }
    };
    $[3] = flatColumnMap;
    $[4] = headerButtonWidth;
    $[5] = store;
    $[6] = tableRef;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const autosizeColumn = t1;
  let t2;
  if ($[8] !== flatColumnMap || $[9] !== headerButtonWidth || $[10] !== store || $[11] !== tableRef) {
    t2 = () => {
      if (!tableRef.current) {
        return;
      }
      const table = tableRef.current;
      const updates = store.getState().columnStates.filter(_temp).reduce((acc, cs_0) => {
        const col_0 = flatColumnMap.get(cs_0.key);
        const width_0 = measureColumnContentWidth(table, cs_0.key, {
          minWidth: col_0?.minWidth,
          headerButtonWidth
        });
        if (width_0 != null) {
          acc[cs_0.key] = width_0;
        }
        return acc;
      }, {});
      store.setColumnStates(store.getState().columnStates.map(cs_1 => cs_1.key in updates ? {
        ...cs_1,
        width: updates[cs_1.key]
      } : cs_1));
    };
    $[8] = flatColumnMap;
    $[9] = headerButtonWidth;
    $[10] = store;
    $[11] = tableRef;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const autosizeAllColumns = t2;
  let t3;
  if ($[13] !== autosizeAllColumns || $[14] !== autosizeColumn) {
    t3 = {
      autosizeColumn,
      autosizeAllColumns
    };
    $[13] = autosizeAllColumns;
    $[14] = autosizeColumn;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
function _temp(cs) {
  return cs.hidden !== true;
}